import { create } from 'zustand'

interface FiatAmount {
  amount: string
  code: string
}

interface CryptoAmount {
  amount: string
  code: string
}

interface EnterAmountScreen {
  isFiatMode: boolean
  triggerShake: boolean
  cryptoAmount: CryptoAmount
  fiatAmount: FiatAmount
}

interface GoalForm extends EnterAmountScreen {
  category: string
  name: string
}

interface GoalTransferIn extends EnterAmountScreen {
  saving_goal_id: string
  category: string
}

interface GoalTransferOut extends EnterAmountScreen {
  saving_goal_id: string
  saving_request_id: string
  total_amount: CryptoAmount
}

interface StoreState {
  savedGoalId: string
  goal: GoalForm
  goalTransferIn: GoalTransferIn
  goalTransferOut: GoalTransferOut

  // Modal states
  isCurrencyModalOpen: boolean
  isBlockchainModalOpen: boolean
}

interface StoreFunctions {
  setSavingGoalId: (goalId: string) => void
  setGoal: (goal: Partial<GoalForm>) => void
  setGoalTransferIn: (goalTransferIn: Partial<GoalTransferIn>) => void
  setGoalTransferOut: (goalTransferOut: Partial<GoalTransferOut>) => void
  resetGoal: () => void
  resetGoalTransferIn: () => void
  resetGoalTransferOut: () => void
  resetAll: () => void

  // Modal functions
  setIsCurrencyModalOpen: (open: boolean) => void
  setIsBlockchainModalOpen: (open: boolean) => void
}

export type SavingsStore = StoreState & StoreFunctions

const defaultEnterAmountScreen: EnterAmountScreen = {
  isFiatMode: false,
  triggerShake: false,
  cryptoAmount: {
    amount: '0',
    code: 'USDT',
  },
  fiatAmount: {
    amount: '0',
    code: 'USD',
  },
}

const initialState: StoreState = {
  savedGoalId:
    typeof window !== 'undefined'
      ? sessionStorage.getItem('savedGoalId') || ''
      : '',

  goal: {
    category: '',
    name: '',
    ...defaultEnterAmountScreen,
  },

  goalTransferIn: {
    saving_goal_id: '',
    category: '',
    ...defaultEnterAmountScreen,
  },

  goalTransferOut: {
    saving_goal_id: '',
    saving_request_id: '',
    total_amount: {
      amount: '0',
      code: 'USDT',
    },
    ...defaultEnterAmountScreen,
  },

  // Modal states
  isCurrencyModalOpen: false,
  isBlockchainModalOpen: false,
}

export const useSavingsStore = create<SavingsStore>()((set) => ({
  ...initialState,

  setSavingGoalId: (goalId) => {
    // Save to sessionStorage for reliability
    // if (goalId) {
    //   sessionStorage.setItem('savedGoalId', goalId)
    // } else {
    //   sessionStorage.removeItem('savedGoalId')
    // }

    set({ savedGoalId: goalId })
  },

  setGoal: (goal) => {
    set((state) => ({ goal: { ...state?.goal, ...goal } }))

    // IF TRIGGER SHAKE, SET IT TO FALSE AFTER 600ms
    if (goal.triggerShake) {
      setTimeout(() => {
        set((state) => ({
          goal: { ...state.goal, triggerShake: false },
        }))
      }, 600)
    }
  },

  setGoalTransferIn: (goalTransferIn) => {
    set((state) => ({
      goalTransferIn: { ...state?.goalTransferIn, ...goalTransferIn },
    }))

    // IF TRIGGER SHAKE, SET IT TO FALSE AFTER 600ms
    if (goalTransferIn.triggerShake) {
      setTimeout(() => {
        set((state) => ({
          goalTransferIn: { ...state.goalTransferIn, triggerShake: false },
        }))
      }, 600)
    }
  },

  setGoalTransferOut: (goalTransferOut) => {
    set((state) => ({
      goalTransferOut: { ...state?.goalTransferOut, ...goalTransferOut },
    }))

    // IF TRIGGER SHAKE, SET IT TO FALSE AFTER 600ms
    if (goalTransferOut.triggerShake) {
      setTimeout(() => {
        set((state) => ({
          goalTransferOut: { ...state.goalTransferOut, triggerShake: false },
        }))
      }, 600)
    }
  },

  resetGoal: () => set({ goal: initialState.goal }),
  resetGoalTransferIn: () =>
    set({ goalTransferIn: initialState.goalTransferIn }),
  resetGoalTransferOut: () =>
    set({ goalTransferOut: initialState.goalTransferOut }),
  resetAll: () => {
    sessionStorage.removeItem('savedGoalId')
    set(initialState)
  },

  // Modal functions
  setIsCurrencyModalOpen: (open: boolean) => set({ isCurrencyModalOpen: open }),
  setIsBlockchainModalOpen: (open: boolean) =>
    set({ isBlockchainModalOpen: open }),
}))
